import { useFormContext } from 'react-hook-form';
import { FormField } from './BaseFormComponents';
import { BaseFormFieldWrapper } from './BaseFormFieldWrapper';
import { Select, SelectOption, SelectProps } from './base-components/Select';
import { FormComponentBaseProps } from './types/form-base-props';

type FormSelectValue = SelectOption['value'] | SelectOption['value'][];

export type FormSelectProps = FormComponentBaseProps & {
  onChange?: (val: FormSelectValue) => void;
} & SelectProps & {
    allowNullValue?: boolean;
    readonly?: boolean;
  };

const FormSelect = ({
  description,
  descriptionProps,
  label,
  labelProps,
  hideError,
  defaultValue,
  name,
  disabled,
  allowNullValue,
  readonly,
  required,
  clearable,
  options,
  ...props
}: FormSelectProps) => {
  const { control, formState } = useFormContext();
  const { isSubmitting } = formState;

  const selectOptions = options.filter(option => option.value !== null);
  const isClearable = clearable ?? allowNullValue;

  return (
    <FormField
      shouldUnregister={false}
      disabled={disabled}
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field }) => (
        <BaseFormFieldWrapper
          description={description}
          descriptionProps={descriptionProps}
          label={label}
          labelProps={labelProps}
          hideError={hideError}
          required={required}
        >
          <Select
            {...props}
            options={selectOptions}
            clearable={isClearable}
            disabled={disabled || readonly || isSubmitting}
            onValueChange={(val: FormSelectValue) => {
              if (!val) return;

              field.onChange(val);
              props.onChange?.(val);
            }}
            {...(isClearable && {
              onClear: () => {
                const clearedValue = allowNullValue ? null : '';
                field.onChange(clearedValue);
                props.onChange?.(clearedValue);
              },
            })}
            {...field}
          />
        </BaseFormFieldWrapper>
      )}
    />
  );
};

export { FormSelect };
